/**
 * lib/examPaperColours.ts — one colour per paper in a shared room.
 * ------------------------------------------------------------------
 * A hall regularly sits three papers at once. On a plan of that hall, which
 * desk belongs to which paper is the question an invigilator handing out
 * scripts is actually asking, and a list of codes in small type does not
 * answer it. So each paper gets a colour, and the desk wears it — on the
 * screen and on the printed plan, from this one table, so the two cannot
 * disagree about which paper is blue.
 *
 * FILLS ARE PALE AND THE INK IS DARK, deliberately: the candidate number is
 * read from the end of a row, and it is read off a photocopy as often as off
 * the original. A saturated fill would win the page and leave the number
 * fighting it. The six are far enough apart to stay distinct in grey.
 *
 * The colour is positional — the papers of a session, sorted, take the
 * palette in turn — so the same session always draws the same way, while a
 * paper that moves to another day is not stuck with yesterday's colour.
 */

export interface PaperColour {
  code: string;
  /** Screen fill and ink. */
  fill: string;
  ink: string;
  /** The same two for jsPDF, which wants 0-255 triples. */
  fillRgb: [number, number, number];
  inkRgb: [number, number, number];
}

const PALETTE: { fill: string; ink: string; fillRgb: [number, number, number]; inkRgb: [number, number, number] }[] = [
  { fill: '#DBEAFE', ink: '#1E3A8A', fillRgb: [219, 234, 254], inkRgb: [30, 58, 138] },   // blue
  { fill: '#FEF3C7', ink: '#92400E', fillRgb: [254, 243, 199], inkRgb: [146, 64, 14] },   // amber
  { fill: '#DCFCE7', ink: '#166534', fillRgb: [220, 252, 231], inkRgb: [22, 101, 52] },   // green
  { fill: '#EDE9FE', ink: '#5B21B6', fillRgb: [237, 233, 254], inkRgb: [91, 33, 182] },   // violet
  { fill: '#FFE4E6', ink: '#9F1239', fillRgb: [255, 228, 230], inkRgb: [159, 18, 57] },   // rose
  { fill: '#CCFBF1', ink: '#115E59', fillRgb: [204, 251, 241], inkRgb: [17, 94, 89] },    // teal
];

/** Grey, for a desk whose paper is not the one being printed. */
export const OTHER_PAPER: PaperColour = {
  code: '', fill: '#F1F2F4', ink: '#6B7280', fillRgb: [241, 242, 244], inkRgb: [107, 114, 128],
};

/**
 * A colour for each paper, in the order the codes sort.
 *
 * A session with ONE paper gets no colour at all: colour that carries no
 * distinction is just decoration, and the plain plan reads better.
 */
export function paperColours(codes: string[]): Map<string, PaperColour> {
  const unique = [...new Set(codes.filter(Boolean))].sort();
  const out = new Map<string, PaperColour>();
  if (unique.length < 2) return out;
  unique.forEach((code, i) => out.set(code, { code, ...PALETTE[i % PALETTE.length] }));
  return out;
}

/** The colour of a candidate sitting one or more papers. */
export function colourFor(
  colours: Map<string, PaperColour>,
  codes: string[],
): PaperColour | null {
  for (const code of codes) {
    const c = colours.get(code);
    if (c) return c;
  }
  return null;
}
